import { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { DeckStatus } from '@digital-twins/shared-types/reporting'
import { toast } from 'sonner'
import i18n from '@/i18n'
import type { SegmentationMapping } from '@/types/components/audiences/segmentation-mapping'
import type { ReportReadiness } from '@/types/routes/test-results'
import {
  connectWebSocket,
  onExperimentProgress,
  onExperimentReadiness,
  onExperimentStatus,
  onExperimentResultsWave,
  subscribeToExperiment,
  unsubscribeFromExperiment,
} from '@/lib/services/websocket'
import { useRealtimeRepair } from '@/lib/realtime/useRealtimeRepair'
import { incrementPollWonRepair } from '@/lib/realtime/pollWonMetrics'
import { AnalyticsEvents, track } from '@/lib/services/analytics'
import {
  defaultStudyReportReadiness,
  describeRegenerationError,
  hasStudyReportResponses,
  type StudyReportExperiment,
  interpretationStillBuilding,
  regenerationErrorMessageKey,
  sectionRetryRequest,
} from '../readiness'
import { servedDeckStatus } from '../reportViewState'
import { studyReportClient } from '../studyReportClient'
import { reportSurveyView } from '@/features/studies/report/utils/reportSurveyView'

/** The section retry a Retry click sends; absent for a full regeneration. */
type SectionRetry = NonNullable<ReturnType<typeof sectionRetryRequest>>

export interface UseStudyReportDataOptions {
  studyId: string
}

export function useStudyReportData({ studyId }: UseStudyReportDataOptions) {
  const [study, setStudy] = useState<StudyReportExperiment | null>(null)
  const [results, setResults] = useState<any>(null)
  const [survey, setSurvey] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [analysisColumns, setAnalysisColumns] = useState<string[]>([])
  const [recommendedAnalysisColumns, setRecommendedAnalysisColumns] = useState<
    string[]
  >([])
  const [segmentationMappings, setSegmentationMappings] = useState<
    SegmentationMapping[] | null
  >(null)
  const [constantContext, setConstantContext] = useState<Record<
    string,
    string
  > | null>(null)
  const [userId, setUserId] = useState<string | undefined>(undefined)
  const [organizationToken, setOrganizationToken] = useState<
    string | undefined
  >(undefined)
  const [storageName, setStorageName] = useState<string | undefined>(undefined)
  const [populationId, setPopulationId] = useState<string | undefined>(undefined)
  const [populationName, setPopulationName] = useState<string | undefined>(
    undefined,
  )
  const [populationCoverUrl, setPopulationCoverUrl] = useState<string | undefined>(undefined)
  // A deck status announced live on the socket, until a reload serves it.
  const [liveDeckStatus, setLiveDeckStatus] = useState<DeckStatus | null>(null)
  const [isRegeneratingSummary, setIsRegeneratingSummary] = useState(false)
  // A full regeneration answers once its generation is allocated and builds
  // on: the generation served at the click, until the report stops generating.
  // `afterLoad`: only a study load started after the answer can tell it ended.
  const [pendingRegeneration, setPendingRegeneration] = useState<{
    servedGenerationId: string | null
    afterLoad: number
  } | null>(null)
  const [isRerunning, setIsRerunning] = useState(false)
  const [readiness, setReadiness] = useState<ReportReadiness>(
    defaultStudyReportReadiness,
  )
  const [liveResponses, setLiveResponses] = useState<Array<Record<string, unknown>> | null>(null)
  const liveResponsesRef = useRef<Array<Record<string, unknown>>>([])

  const mountedRef = useRef(true)
  const loadStudySeqRef = useRef(0)
  const appliedLoadSeqRef = useRef(0)
  const resultsPageTrackedRef = useRef(false)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!study?.id || resultsPageTrackedRef.current) return
    if (!readiness.isReady || !results?.responses?.length) return
    resultsPageTrackedRef.current = true
    track(AnalyticsEvents.RESULTS_PAGE_VIEWED, {
      experiment_id: studyId,
      status: study.status,
    })
  }, [study?.id, study?.status, studyId, readiness.isReady, results])

  const loadStudy = useCallback(async () => {
    const seq = ++loadStudySeqRef.current
    try {
      const full = await studyReportClient.getFull(studyId)
      if (seq !== loadStudySeqRef.current) return
      appliedLoadSeqRef.current = seq
      const exp = full.experiment as StudyReportExperiment
      const population = full.population as any
      setStudy(exp)
      setReadiness(full.readiness || defaultStudyReportReadiness)

      if (exp.userId) setUserId(exp.userId)
      else if (exp.user?.id) setUserId(exp.user.id)
      else {
        try {
          const userStr = localStorage.getItem('user')
          if (userStr) {
            const user = JSON.parse(userStr)
            if (user.id) setUserId(user.id)
          }
        } catch {
          console.warn('Failed to get user from local storage')
        }
      }

      if (exp.organizationToken) {
        setOrganizationToken(exp.organizationToken)
      } else {
        try {
          const userStr = localStorage.getItem('user')
          if (userStr) {
            const user = JSON.parse(userStr)
            if (user.organizationToken)
              setOrganizationToken(user.organizationToken)
          }
        } catch {
          console.warn('Failed to get organizationToken from local storage')
        }
      }

      const populationMappings: SegmentationMapping[] | null =
        Array.isArray(population?.segmentationMappings) &&
        population.segmentationMappings.length > 0
          ? population.segmentationMappings
          : null

      setSegmentationMappings(populationMappings)
      setConstantContext(population?.constantContext || null)

      const rawRecommended = population?.recommendedAnalysisColumns
      let resolvedAnalysisColumns: string[] = population?.analysisColumns || []
      let resolvedRecommended: string[] = []

      if (Array.isArray(rawRecommended) && rawRecommended.length > 0) {
        if (
          typeof rawRecommended[0] === 'object' &&
          rawRecommended[0]?.sourceColumn
        ) {
          resolvedRecommended = (rawRecommended as SegmentationMapping[]).map(
            (m) => m.sourceColumn,
          )
        } else {
          resolvedRecommended = rawRecommended as string[]
        }
      }

      if (populationMappings) {
        resolvedAnalysisColumns = populationMappings.map((m) => m.sourceColumn)
      }

      setAnalysisColumns(resolvedAnalysisColumns)
      setRecommendedAnalysisColumns(resolvedRecommended)
      setPopulationId(population?.id ?? exp.populationId ?? undefined)
      setPopulationName(population?.name)
      setPopulationCoverUrl(population?.coverImageUrl ?? undefined)
      setStorageName(full.storage?.name)

      if (exp.status === 'completed') {
        setResults(full.results)
        // The report's view of the questions: names, never the ordinals
        // twins read (`reportSurveyView`).
        setSurvey(reportSurveyView(exp.surveyJson ?? null))

      } else {
        setResults(null)
        setSurvey(null)
      }
    } catch (error) {
      if (seq !== loadStudySeqRef.current) return
      console.error('Failed to load study report:', error)
      setResults(null)
      setSurvey(null)
      setReadiness(defaultStudyReportReadiness)
    } finally {
      if (seq === loadStudySeqRef.current) setLoading(false)
    }
  }, [studyId])

  useEffect(() => {
    loadStudy()
    subscribeToExperiment(studyId)

    const progressHandler = (data: any) => {
      setStudy((prev) =>
        prev
          ? {
              ...prev,
              progressPercent: data.progress_percent || prev.progressPercent,
              respondentsCompleted:
                data.respondents_completed || prev.respondentsCompleted,
            }
          : null,
      )
    }

    const statusHandler = (data: { status: string } | string) => {
      const status = typeof data === 'string' ? data : data.status

      if (status === 'deck_failed') {
        toast.error(i18n.t('studyReport.deckFailed', { ns: 'app' }), {
          id: reportFailureToastId(studyId),
        })
        setLiveDeckStatus(null)
      }

      // The report failed with its deck readable or not expected (a failed
      // Summary): never told as a deck failure.
      if (status === 'report_failed') {
        toast.error(i18n.t('studyReport.reportFailed', { ns: 'app' }), {
          id: reportFailureToastId(studyId),
        })
        setLiveDeckStatus(null)
      }

      if (status === 'deck_processing') {
        setLiveDeckStatus('processing')
      }

      if (status === 'report_ready') {
        setLiveDeckStatus(null)
        liveResponsesRef.current = []
        setLiveResponses(null)
      }

      loadStudy()
    }

    const resultsWaveHandler = (data: { responses: Array<Record<string, unknown>> }) => {
      if (!data?.responses?.length) return
      // Debug: log first response shape to verify survey_data presence
      if (liveResponsesRef.current.length === 0 && data.responses[0]) {
        console.debug('[live-stream] first response keys:', Object.keys(data.responses[0]))
        const sd = (data.responses[0] as Record<string, unknown>).survey_data
        if (sd && typeof sd === 'object') {
          console.debug('[live-stream] survey_data keys:', Object.keys(sd).slice(0, 5))
        } else {
          console.debug('[live-stream] NO survey_data — top-level value sample:', Object.entries(data.responses[0]).slice(0, 3))
        }
      }
      const updated = [...liveResponsesRef.current, ...data.responses]
      liveResponsesRef.current = updated
      setLiveResponses(updated)
    }

    const offProgress = onExperimentProgress(studyId, progressHandler)
    const offStatus = onExperimentStatus(studyId, statusHandler)
    const offResultsWave = onExperimentResultsWave(studyId, resultsWaveHandler)

    return () => {
      offProgress?.()
      offStatus?.()
      offResultsWave?.()
      unsubscribeFromExperiment(studyId)
    }
  }, [loadStudy, studyId])

  const awaitingReadinessSync =
    isRegeneratingSummary ||
    pendingRegeneration !== null ||
    (Boolean(study) &&
      study!.status === 'completed' &&
      readiness.overallStatus !== 'failed' &&
      (!readiness.isReady ||
        !hasStudyReportResponses(results) ||
        readiness.steps.deck.status === 'processing' ||
        interpretationStillBuilding(readiness.availability)))

  // The one owner of "after a gap": a reconnect after a real drop (2 s or
  // more) restarts the live stream, since waves sent while away are lost, and
  // reloads the study. The first connect does neither: the mount already loaded.
  const repairReadiness = useCallback(
    (reason: 'reconnect_gap' | 'visibility_gap' | 'stall_timeout') => {
      incrementPollWonRepair('REPORT', reason)
      if (reason === 'reconnect_gap') {
        liveResponsesRef.current = []
        setLiveResponses(null)
      }
      void loadStudy()
    },
    [loadStudy],
  )

  const { notifyActivity } = useRealtimeRepair({
    active: awaitingReadinessSync,
    onRepair: repairReadiness,
  })

  useEffect(() => {
    connectWebSocket()
    const handleReadiness = () => {
      notifyActivity()
      void loadStudy().then(() => {
        if (mountedRef.current && isRegeneratingSummary) {
          setIsRegeneratingSummary(false)
        }
      })
    }
    const offReadiness = onExperimentReadiness(studyId, handleReadiness)
    return () => {
      offReadiness()
    }
  }, [isRegeneratingSummary, loadStudy, notifyActivity, studyId])

  // A Retry keeps the served report on screen (no optimistic "not ready"):
  // only the Retry button spins while the request runs, and the study is
  // reloaded from what the server serves once it answers. A full
  // regeneration answers once its generation is allocated: the button keeps
  // spinning until the report stops generating.
  const requestRegeneration = useCallback(
    async (sectionRetry: SectionRetry | undefined): Promise<void> => {
      const servedGenerationId = readiness.availability?.reportGenerationId ?? null
      let buildStarted = false
      try {
        setIsRegeneratingSummary(true)
        await studyReportClient.regenerateReport(studyId, sectionRetry)
        buildStarted = !sectionRetry
        track(AnalyticsEvents.EXPERIMENT_REPORT_REGENERATED, {
          experiment_id: studyId,
        })
      } catch (error) {
        console.error('Failed to regenerate summary:', error)
        const described = describeRegenerationError(error)
        // Always the reader's language: never the server's message.
        const message = i18n.t(
          regenerationErrorMessageKey(described, { isSectionRetry: Boolean(sectionRetry) }),
          { ns: 'app' },
        )
        toast.error(message, {
          id: reportFailureToastId(studyId),
          ...(sectionRetry && described.offersFullRegeneration
            ? {
                action: {
                  label: i18n.t('studyReport.regenerateFullReport', { ns: 'app' }),
                  onClick: () => requestRegeneration(undefined),
                },
              }
            : {}),
        })
      } finally {
        const afterLoad = loadStudySeqRef.current
        await loadStudy()
        if (mountedRef.current) {
          if (buildStarted) setPendingRegeneration({ servedGenerationId, afterLoad })
          setIsRegeneratingSummary(false)
        }
      }
    },
    [loadStudy, readiness.availability, studyId],
  )

  // The regeneration ended (the report no longer generating, told by the
  // status and readiness events or the stall reload). It changed nothing
  // served and the report did not fail: the previous report is still shown,
  // so say it did not finish. A failed report is told by its own toast.
  useEffect(() => {
    if (
      !pendingRegeneration ||
      appliedLoadSeqRef.current <= pendingRegeneration.afterLoad ||
      !study ||
      study.reportStatus === 'generating'
    ) {
      return
    }
    setPendingRegeneration(null)
    const served = readiness.availability?.reportGenerationId ?? null
    const failed = study.reportStatus === 'failed' || study.deckStatus === 'failed'
    if (served === pendingRegeneration.servedGenerationId && !failed) {
      toast.error(i18n.t('studyReport.retryFailed', { ns: 'app' }), {
        id: reportFailureToastId(studyId),
      })
    }
  }, [pendingRegeneration, readiness.availability, study, studyId])

  const regenerateSummary = useCallback(async () => {
    // Unfinished sections of the accepted report retry alone; anything
    // else regenerates the whole report.
    await requestRegeneration(
      sectionRetryRequest(readiness.availability) ?? undefined,
    )
  }, [readiness.availability, requestRegeneration])

  const rerunStudy = useCallback(async (): Promise<string | null> => {
    try {
      setIsRerunning(true)
      const result = await studyReportClient.rerun(studyId)
      track(AnalyticsEvents.EXPERIMENT_RERUN, {
        experiment_id: studyId,
        new_experiment_id: result.id,
      })
      // Neutral copy: simulation may still fail downstream; success here only
      // means the rerun request was accepted.
      toast.success(i18n.t('studyReport.retrying', { ns: 'app' }))
      return result.id ?? null
    } catch (error) {
      console.error('Failed to rerun study:', error)
      const fallback = i18n.t('studyReport.retryStudyFailed', { ns: 'app' })
      const detail = axios.isAxiosError(error)
        ? error.response?.data?.message ?? fallback
        : fallback
      toast.error(detail)
      return null
    } finally {
      if (mountedRef.current) setIsRerunning(false)
    }
  }, [studyId])

  const deckStatus = servedDeckStatus({
    study: study?.deckStatus,
    deckStep: readiness.steps.deck.status,
    live: liveDeckStatus,
  })

  return {
    study,
    results,
    survey,
    loading,
    analysisColumns,
    recommendedAnalysisColumns,
    segmentationMappings,
    constantContext,
    userId,
    organizationToken,
    storageName,
    populationId,
    populationName,
    populationCoverUrl,
    deckStatus,
    isRegeneratingSummary: isRegeneratingSummary || pendingRegeneration !== null,
    isRerunning,
    readiness,
    liveResponses,
    loadStudy,
    regenerateSummary,
    rerunStudy,
  }
}

/** One toast per study for a report failure: a typed retry error replaces the live one. */
function reportFailureToastId(studyId: string): string {
  return `report-failure-${studyId}`
}
